import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  LayoutDashboard, ClipboardList, Hand, FolderKanban, Users2, UserCog, Link2,
  Megaphone, HandCoins, Building2, Trophy, LogOut, ArrowLeftRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Brand } from "@/components/Brand";
import { useAuth } from "@/context/AuthContext";
import { VOLUNTEER_ROLE_LABELS, isManager } from "@/lib/roles";
import MyCampaigns from "@/components/volunteer/MyCampaigns";
import MyWork from "@/components/volunteer/MyWork";
import UpForGrabs from "@/components/volunteer/UpForGrabs";
import ProjectsManage from "@/components/volunteer/ProjectsManage";
import VolunteerDirectory from "@/components/volunteer/VolunteerDirectory";
import AccountsManage from "@/components/volunteer/AccountsManage";
import InvitesTab from "@/components/admin/InvitesTab";
import OverviewTab from "@/components/admin/OverviewTab";
import LeaderboardTab from "@/components/admin/LeaderboardTab";
import CampaignsTab from "@/components/admin/CampaignsTab";
import DonationsTab from "@/components/admin/DonationsTab";
import SponsorsTab from "@/components/admin/SponsorsTab";

export default function VolunteerPortal() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const manager = isManager(user);
  const perms = user?.permissions || {};

  const tabs = [
    { id: "campaigns_view", label: "My Campaigns", icon: LayoutDashboard, C: MyCampaigns },
    { id: "mywork", label: "My Work", icon: ClipboardList, C: MyWork },
    { id: "grabs", label: "Up for Grabs", icon: Hand, C: UpForGrabs },
  ];
  if (manager) {
    tabs.push(
      { id: "projects", label: "Projects", icon: FolderKanban, C: ProjectsManage },
      { id: "volunteers", label: "Volunteers", icon: Users2, C: VolunteerDirectory },
      { id: "accounts", label: "Accounts", icon: UserCog, C: AccountsManage },
      { id: "invites", label: "Invites", icon: Link2, C: InvitesTab },
      { id: "overview", label: "Fundraising", icon: LayoutDashboard, C: OverviewTab },
      { id: "leaderboard", label: "Leaderboard", icon: Trophy, C: LeaderboardTab },
    );
    if (user.role === "admin" || perms.manage_campaigns) tabs.push({ id: "campaigns", label: "Campaigns", icon: Megaphone, C: CampaignsTab });
    if (user.role === "admin" || perms.manage_donations) tabs.push({ id: "donations", label: "Donations", icon: HandCoins, C: DonationsTab });
    if (user.role === "admin" || perms.manage_sponsors) tabs.push({ id: "sponsors", label: "Sponsors", icon: Building2, C: SponsorsTab });
  }

  const [active, setActive] = useState("campaigns_view");
  const Active = (tabs.find((t) => t.id === active) || tabs[0]).C;
  let roleLabel = "Volunteer";
  if (user?.volunteer_role) roleLabel = VOLUNTEER_ROLE_LABELS[user.volunteer_role];
  else if (user?.role === "admin") roleLabel = "Staff Admin";

  const doLogout = () => { logout(); navigate("/"); };

  const NavBtn = ({ t }) => (
    <button onClick={() => setActive(t.id)} data-testid={`volunteer-nav-${t.id}`}
      className={`flex shrink-0 items-center gap-2.5 rounded-xl px-3.5 py-2.5 text-sm font-semibold transition-colors ${active === t.id ? "bg-[#00334a] text-white shadow-sm" : "text-[#3f6670] hover:bg-[#efe6d6]"}`}>
      <t.icon className="h-[18px] w-[18px]" /> <span className="whitespace-nowrap">{t.label}</span>
    </button>
  );

  return (
    <div className="min-h-screen bg-[#f8f3e9]">
      <header className="sticky top-0 z-40 hf-glass border-b border-[#e4d8c4]/70">
        <div className="mx-auto flex max-w-[1400px] items-center justify-between px-5 py-3">
          <div className="flex items-center gap-3">
            <Brand />
            <span className="hidden rounded-full bg-[#efe6d6] px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[#3f6670] sm:inline">{roleLabel}</span>
          </div>
          <div className="flex items-center gap-2">
            {user?.role === "admin" && (
              <Button variant="outline" size="sm" onClick={() => navigate("/admin")} className="rounded-full border-[#e4d8c4] text-[#3f6670]" data-testid="switch-to-admin">
                <ArrowLeftRight className="mr-1.5 h-4 w-4" /> Staff portal
              </Button>
            )}
            {user?.role === "supporter" && (
              <Button variant="outline" size="sm" onClick={() => navigate("/portal")} className="rounded-full border-[#e4d8c4] text-[#3f6670]" data-testid="switch-to-portal">
                <ArrowLeftRight className="mr-1.5 h-4 w-4" /> Fundraising
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={doLogout} className="rounded-full border-[#e4d8c4] text-[#3f6670]" data-testid="volunteer-logout-button">
              <LogOut className="mr-1.5 h-4 w-4" /> Sign out
            </Button>
          </div>
        </div>
      </header>

      <div className="mx-auto flex max-w-[1400px] gap-6 px-5 py-6">
        <aside className="hidden w-60 shrink-0 lg:block">
          <nav className="sticky top-20 space-y-1">{tabs.map((t) => <NavBtn key={t.id} t={t} />)}</nav>
        </aside>
        <main className="min-w-0 flex-1">
          <div className="mb-5 flex gap-2 overflow-x-auto pb-1 lg:hidden">{tabs.map((t) => <NavBtn key={t.id} t={t} />)}</div>
          <Active />
        </main>
      </div>
    </div>
  );
}
